import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import { Button } from "components/Button";
import { DEFAULT_TRADE_DAYS } from "utils/constants";
import { TradeDaysEditor } from "./TradeDaysEditor";
import {
  ScheduleModalContainer,
  ScheduleHeader,
  ScheduleBody,
  ScheduleFooter,
} from "./ScheduleModal.styles";
import { useBuilderReadOnly } from "features/Strategy/v2/BuilderReadOnly";

/**
 * Prompts the user to set the days/hours their strategy will run, shown when
 * they click Deploy. Lets them toggle each day (is_active) and pick the
 * start_date / end_date times before continuing to the deploy flow.
 */
export const ScheduleModal = ({
  show,
  onHide,
  initialDays,
  onConfirm,
  isSaving = false,
}) => {
  const [days, setDays] = useState([]);
  const readOnly = useBuilderReadOnly();

  useEffect(() => {
    if (!show) return;
    const base =
      Array.isArray(initialDays) && initialDays.length
        ? initialDays
        : DEFAULT_TRADE_DAYS;
    // Clone so editing never mutates the caller's data or the shared default.
    setDays(JSON.parse(JSON.stringify(base)));
  }, [show, initialDays]);

  const updateDay = (idx, key, value) => {
    if (readOnly) return;

    setDays((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value };
      return next;
    });
  };

  const hasActiveDay = useMemo(() => days.some((day) => day.is_active), [days]);

  return (
    <ScheduleModalContainer heading="" show={show} onHide={onHide}>
      <ScheduleHeader>
        <h2>Set run schedule</h2>
      </ScheduleHeader>

      <ScheduleBody>
        <p className="description">
          Choose the days of the week and hours each day that your strategy will
          run. Days left unchecked won&apos;t trade.
        </p>

        <TradeDaysEditor days={days} onChange={updateDay} disabled={readOnly} />
      </ScheduleBody>

      <ScheduleFooter>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => onConfirm(days)}
          disabled={readOnly || !hasActiveDay}
          isLoading={isSaving}
        >
          {!isSaving && "Continue to deploy"}
        </Button>
      </ScheduleFooter>
    </ScheduleModalContainer>
  );
};

ScheduleModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  initialDays: PropTypes.array,
  onConfirm: PropTypes.func,
  isSaving: PropTypes.bool,
};
